import { ForwardedRef, forwardRef, type ReactElement, type ReactNode } from 'react';

import shouldForwardProp from '@styled-system/should-forward-prop';
import isNil from 'lodash/isNil';
import styled, { css } from 'styled-components';

import type { Space } from '../../../theme/space';

type TextGroupContainerProps = Readonly<{
  gap: Space;
  hideOverflow: boolean;
  fitToContainer: boolean;
  align?: 'left' | 'right';
}>;

const textGroupAlignment = ({ align }: TextGroupContainerProps) => {
  if (isNil(align)) {
    return null;
  }

  if (align === 'right') {
    return css`
      justify-items: end;
    `;
  }

  return css`
    justify-items: start;
  `;
};

const textGroupGap = ({ gap }: TextGroupContainerProps) => css`
  row-gap: ${({ theme }) => theme.scTheme.space[gap]};
`;

const TextGroupContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => shouldForwardProp(prop),
})<TextGroupContainerProps>`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: auto;
  ${textGroupGap};
  ${textGroupAlignment};
  ${({ hideOverflow, fitToContainer }) => hideOverflow && css`
    > * {
      display: inline-block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      width: ${fitToContainer ? '100%' : null};
    }
  `}
`;

export type Props = Readonly<{
  children: ReactNode;
  hideOverflow?: boolean;
  fitToContainer?: boolean;
  gap?: Space;
  align?: TextGroupContainerProps['align'];
}>;

const TextGroup = forwardRef(({
  children, hideOverflow = false, gap = 100, align, fitToContainer = false,
}: Props, ref: ForwardedRef<HTMLDivElement>): ReactElement => (
  <TextGroupContainer ref={ref} gap={gap} hideOverflow={hideOverflow} align={align} fitToContainer={fitToContainer}>
    {children}
  </TextGroupContainer>
));

export default TextGroup;
